import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { MINIMUM_ACCEPTED_AUDIO_DURATION } from '@modals/PriceModal/constants';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { resolveOrganizationBillingAccountId } from '@helpers/resolveOrganizationBillingAccountId';
import { useAPI } from '@providers/useAPI';
import { useAuthContext } from '@providers/AuthProvider';
import { BillingService, PaymentService } from 'app/API';
import { useEffect, useState } from 'react';

import { PriceModalErrors } from './components/errors';
import { PriceModalNoPriceDescription } from './components/noPriceDescription';
import { PriceModalPriceDescription } from './components/priceDescription';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { Content } from './styles/Content';
import { TProps } from './types/TProps';
import { Button } from '@components/core-ui/form-elements/buttons/Button';

export function PriceModal({
  isSubmitting,
  setPaymentIntent,
  paymentIntent,
  onCancel,
  onSuccess,
  jobId,
  enhancedPaymentVariant,
  freeTrialInEffect,
}: TProps) {
  const { call } = useAPI();
  const { updateMe, organization } = useAuthContext();

  const [price, setPrice] = useState<{ price: number; ASRRate: string; duration?: number }>({ price: 0, ASRRate: '0' });
  const [paymentMethodId, setPaymentMethodId] = useState<string>();
  const [dataLoaded, setDataLoaded] = useState(false);
  const [balanceIsRefreshing, setBalanceIsRefreshing] = useState(false);

  const [audioDuration, setAudioDuration] = useState(0);

  const balance = organization?.billingAccount?.currentBalance ?? 0;
  const notEnoughFunds = (price?.price ?? 0) > balance;
  const cannotViewBilling = !organization?.permissions?.viewBilling;
  const noBillingAccount = !resolveOrganizationBillingAccountId(organization);
  const isAutoTopUpEnabled = organization?.billingAccount?.AutoTopUpSettings?.enabled;

  useEffect(() => {
    setDataLoaded(false);

    const load = async () => {
      const meData = await updateMe();
      const org = meData?.organizations?.find((item) => item.id === organization?.id) ?? organization;
      const baId = resolveOrganizationBillingAccountId(org);

      if (baId) {
        const methods = await call(PaymentService.billingAccountPaymentMethodListForJobSubmission({ baId }));
        setPaymentMethodId(methods?.find((item) => item.default)?.id);
      }
      const priceData = enhancedPaymentVariant
        ? await call(BillingService.calculateJobEnhancementPricing({ jobId }))
        : await call(BillingService.calculateJobPricing({ jobId }));

      setAudioDuration(priceData?.result?.duration || 0);

      if (priceData?.result) {
        setPrice({
          ...priceData.result,
          ASRRate: String(priceData.result.ASRRate),
        });
      }

      setDataLoaded(true);
    };

    void load();
  }, [
    enhancedPaymentVariant,
    jobId,
    organization?.id,
    organization?.billingAccount?.id,
    organization?.billingAccountId,
  ]);

  const loading = isSubmitting || !dataLoaded;
  const error = noBillingAccount || (!paymentMethodId && notEnoughFunds) || (notEnoughFunds && !isAutoTopUpEnabled);

  if (loading) {
    return (
      <ModalWrapper width="390px">
        {loading && <AbsoluteSpinner overlay={false} />}
        <ModalTitle>Confirm</ModalTitle>
        <Content></Content>
      </ModalWrapper>
    );
  }

  return (
    <ModalWrapper width="390px" className="min-h-[100px]" onCancel={onCancel}>
      <ModalTitle>Confirm</ModalTitle>
      <Content>
        <div data-testid="priceDescription">
          {cannotViewBilling ? (
            <PriceModalNoPriceDescription price={price} />
          ) : (
            <PriceModalPriceDescription
              price={price}
              setDataLoaded={setDataLoaded}
              balanceIsRefreshing={balanceIsRefreshing}
              setBalanceIsRefreshing={setBalanceIsRefreshing}
              audioDuration={audioDuration}
              enhancedPaymentVariant={enhancedPaymentVariant}
              freeTrialInEffect={freeTrialInEffect}
            />
          )}
        </div>

        <div className={`errorsWrapper ${error ? 'active' : ''}`}>
          <PriceModalErrors
            setBalanceIsRefreshing={setBalanceIsRefreshing}
            price={price}
            onCancel={onCancel}
            setPaymentIntent={setPaymentIntent}
            paymentIntent={paymentIntent}
            dataLoaded={dataLoaded}
            setDataLoaded={setDataLoaded}
            onSuccess={onSuccess}
            jobId={jobId}
            isSubmitting={isSubmitting}
            paymentMethodId={paymentMethodId}
          />
        </div>

        <div className="buttonWrapper">
          <OutlinedButton className="relative w-full" onClick={onCancel}>
            Cancel
          </OutlinedButton>

          <Button
            className="relative w-full"
            data-testid="priceModalSubmitButton"
            disabled={loading || audioDuration < MINIMUM_ACCEPTED_AUDIO_DURATION || error}
            onClick={() => {
              setDataLoaded(false);
              onSuccess();
              setTimeout(updateMe, 1000);
            }}
          >
            {enhancedPaymentVariant ? 'Enhance' : 'Transcribe'}
          </Button>
        </div>
      </Content>
    </ModalWrapper>
  );
}
